<template>
  <v-app>
    <v-container fluid>
      <!-- 🔹 Search Form -->
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Search Form
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="submit">
                  <v-row class="pa-2">
                    <!-- Employee Selector -->
                    <v-col lg="3" md="4" cols="12">
                      <v-autocomplete
                        v-model="employee.value.value"
                        :items="getHrmEmployeeList"
                        :item-title="item => `${item.code}-${item.name}` ?? 'N/A'"
                        item-value="id"
                        label="Select Employee"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        clearable
                        :loading="loading"
                        :error-messages="employee.errorMessage.value"
                        prepend-inner-icon="mdi-account"
                      />
                    </v-col>

                    <!-- Year Selector -->
                    <v-col lg="3" md="4" cols="12">
                      <v-autocomplete
                        v-model="year.value.value"
                        :error-messages="year.errorMessage.value"
                        :items="getYearList"
                        item-title="label"
                        item-value="value"
                        variant="outlined"
                        label="Enter Year"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar-month"
                        clearable
                      ></v-autocomplete>
                    </v-col>
                    <!-- Submit Button -->
                    <v-col lg="2" md="4" cols="12">
                      <v-btn class="bg-brand w-15" type="submit" ref="searchButton">Search</v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- 🔹 Employee Leave Table -->
      <v-row>
        <v-col cols="12">
          <table style="width: 100%; font-size: 13px; border-collapse: collapse; margin-bottom: 6px;">
            <thead>
              <tr style="background-color: #f5f5f5; text-align: left;">
                <th style="width: 10%; text-align: left;">#SL</th>
                <th style="width: 10%; text-align: left;">Name</th>
                <th style="width: 10%; text-align: left;">Code</th>
                <th style="width: 10%; text-align: left;">Designation</th>
                <th style="width: 10%; text-align: left;">Department</th>
                <th style="width: 10%; text-align: left;">Action</th>
              </tr>
            </thead>
          </table>

          <v-expansion-panels focusable>
            <v-expansion-panel
              v-for="(employee, index) in getLeaveBalanceList"
              :key="employee.employee_id"
            >
              <v-expansion-panel-title>
                <table style="width: 100%; font-size: 13px; border-collapse: collapse;">
                  <tbody>
                    <tr>
                      <td style="width: 5%; text-align: center;">{{ index + 1 }}</td>
                      <td style="width: 10%; text-align: center;">{{ employee.employee_name }}</td>
                      <td style="width: 10%; text-align: center;">{{ employee.employee_code }}</td>
                      <td style="width: 10%; text-align: center;">{{ employee.designation?.name }}</td>
                      <td style="width: 10%; text-align: center;">{{ employee.department?.name }}</td>
                      <td style="width: 10%; text-align: center;">
                        <v-icon class="me-2 edit-button" color="primary" @click="editItem(employee)" title="Edit">
                            mdi-pencil-outline
                        </v-icon>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <!-- Search & Export -->
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search Leave Type"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      clearable
                    />
                  </v-col>
                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV(employee)"
                        >CSV</v-btn>
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel(employee)"
                        >EXCEL</v-btn>
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF(employee)"
                        >PDF</v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>
                </v-row>

                <!-- Leave Balance Table -->
                <v-data-table
                  :headers="leaveHeaders"
                  :items="employee.leave_balances.filter(l => !search || l.leave_type_name.toLowerCase().includes(search.toLowerCase()))"
                  :loading="loading"
                  density="compact"
                  hide-default-footer
                >
                  <template v-slot:item.index="{ index }">{{ index + 1 }}</template>
                </v-data-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { ref, onMounted  } from "vue";
import { toast } from "vue3-toastify";
import { useRouter } from 'vue-router'

export default {
  data: () => ({
    panel: [0],
    search: "",
    loading: false,
    leaveHeaders: [
      { title: "#SL", key: "index", align: "center", sortable: false },
      { title: "Leave Type", key: "leave_type_name", align: "left", sortable: true },
      { title: "Entitled Days", key: "entitled_days", align: "right", sortable: true },
      { title: "Used Days", key: "used_days", align: "right", sortable: true },
      { title: "Carried Forward", key: "carried_forward", align: "right", sortable: true },
      { title: "Remaining Days", key: "remaining_days", align: "right", sortable: true },
    ],
  }),

  computed: {
    getHrmEmployeeList() {
      return this.$store.getters.getHrmEmployeeList;
    },
    getLeaveBalanceList() {
      const data = this.$store.getters.getLeaveBalanceList;
      return data;
    },
    getYearList() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = currentYear - 5; i <= currentYear + 5; i++) {
          years.push(i);
      }
      return years;
    },
  },

  created() {
    if (!this.$store.getters.getHrmEmployeeList.length) {
      this.loading = true;
      this.$store.dispatch("getHrmEmployeeList")?.finally(() => (this.loading = false));
    }

    // if (!this.$store.getters.getLeaveBalanceList.length) {
      this.loading = true;
      this.$store.dispatch("getLeaveBalanceList")
        ?.finally(() => (this.loading = false));
    // }
  },

  setup() {
    const store = useStore();
    const { handleSubmit } = useForm({ validationSchema: yup.object({}) });
    const employee = useField("employee");
    const year = useField("year");
    const loading = ref(false);
    const router = useRouter()

    const submit = handleSubmit(() => {
      loading.value = true;
      const payload = {
        employee_id: employee.value.value || null,
        year: year.value.value || null,
      };
      store.dispatch("getLeaveBalanceList", payload) 
        ?.finally(() => (loading.value = false))
        .catch((err) =>
          toast.error(err.response?.data?.message || "Error fetching leave data")
        );
    });

    const editItem = (employee) => {
      const selectedYear = year.value?.value
      store.commit('SET_SELECTED_EMPLOYEE_LEAVE_BALANCE', {
        ...employee,
        year: selectedYear
      })
      router.push({name: 'employee-leave-process'});
    };

    onMounted(() => {
      year.value.value = new Date().getFullYear()
    })

    return { submit, employee, year, loading, editItem };


  },

  methods: {
    // ✅ CSV Export
    exportToCSV(employee) {
      const data = [
        ["#SL", "Leave Type", "Entitled Days", "Used Days", "Carried Forward", "Remaining Days"],
      ];
      let serial = 1;
      employee.leave_balances.forEach(item => {
        data.push([
          serial++,
          item.leave_type_name || "-",
          item.entitled_days || 0,
          item.used_days || 0,
          item.carried_forward || 0,
          item.remaining_days || 0,
        ]);
      });

      const csv = data.map(row => row.join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", `leave_balance_${employee.employee_code}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // ✅ Excel Export (simple CSV rename)
    exportToExcel(employee) {
      const data = [
        ["#SL", "Leave Type", "Entitled Days", "Used Days", "Carried Forward", "Remaining Days"],
      ];
      let serial = 1;
      employee.leave_balances.forEach(item => {
        data.push([
          serial++,
          item.leave_type_name || "-",
          item.entitled_days || 0,
          item.used_days || 0,
          item.carried_forward || 0,
          item.remaining_days || 0,
        ]);
      });

      const csv = data.map(row => row.join(",")).join("\n");
      const blob = new Blob([csv], { type: "application/vnd.ms-excel" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", `leave_balance_${employee.employee_code}.xls`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // ✅ PDF Export using jsPDF
    async exportToPDF(employee) {
      const { jsPDF } = await import("jspdf");
      const autoTable = (await import("jspdf-autotable")).default;

      const doc = new jsPDF();
      doc.text(`Leave Balance - ${employee.employee_name}`, 14, 10);

      const columns = ["#SL", "Leave Type", "Entitled", "Used", "Carried", "Remaining"];
      const rows = employee.leave_balances.map((item, i) => [
        i + 1,
        item.leave_type_name,
        item.entitled_days,
        item.used_days,
        item.carried_forward,
        item.remaining_days,
      ]);

      autoTable(doc, {
        head: [columns],
        body: rows,
        startY: 20,
      });

      doc.save(`leave_balance_${employee.employee_code}.pdf`);
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.$refs.searchButton?.$el?.click();
    });
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
table th,
table td {
  padding: 4px 8px;
}
@media (max-width: 600px) {
  table {
    font-size: 12px;
  }
}
</style>
